import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import type { Cart, CartItem } from '@/types'
import { localStorageGet, localStorageSet, localStorageDelete } from '@/lib/storage'
import { calculateCartSubtotal } from '@/lib/formatPrice'

// ============================================================================
// Cart Context — a SINGLE shared cart state for the whole app.
// Previously `useCart` held its own state per call site, so the StickyBar's
// cart count went stale whenever MenuPage added an item (separate instances).
// This provider fixes that: every consumer reads/writes the same cart.
// ============================================================================

const CART_STORAGE_KEY = 'qr-menu-cart'
const CART_EXPIRY_MS = 2 * 60 * 60 * 1000 // 2 hours
const MAX_ITEMS = 20
const MAX_QUANTITY = 20

export interface UseCartReturn {
  cart: Cart | null
  addItem: (item: Omit<CartItem, 'quantity'>) => void
  removeItem: (index: number) => void
  updateQuantity: (index: number, quantity: number) => void
  clearCart: () => void
  itemCount: number
  subtotal: number
}

const CartContext = createContext<UseCartReturn | undefined>(undefined)

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null)

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorageGet<{ cart: Cart; timestamp: number } | null>(
        CART_STORAGE_KEY,
        null
      )

      if (stored) {
        const now = Date.now()
        if (now - stored.timestamp > CART_EXPIRY_MS) {
          localStorageDelete(CART_STORAGE_KEY)
          setCart(null)
        } else {
          setCart(stored.cart)
        }
      }
    } catch (error) {
      console.error('Failed to load cart:', error)
    }
  }, [])

  // Persist whenever the shared cart changes
  useEffect(() => {
    if (cart) {
      localStorageSet(CART_STORAGE_KEY, { cart, timestamp: Date.now() })
    } else {
      localStorageDelete(CART_STORAGE_KEY)
    }
  }, [cart])

  const addItem = useCallback((item: Omit<CartItem, 'quantity'>) => {
    setCart((prevCart) => {
      const items = prevCart?.items || []

      if (items.length >= MAX_ITEMS) return prevCart

      const existingIndex = items.findIndex(
        (existing) =>
          existing.itemId === item.itemId &&
          JSON.stringify(existing.variant) === JSON.stringify(item.variant) &&
          JSON.stringify(existing.modifiers) === JSON.stringify(item.modifiers)
      )

      if (existingIndex >= 0) {
        const updated = [...items]
        if (updated[existingIndex].quantity < MAX_QUANTITY) {
          updated[existingIndex].quantity += 1
        }
        return { ...prevCart, items: updated, subtotal: calculateCartSubtotal(updated) } as Cart
      }

      const newItems = [...items, { ...item, quantity: 1 }]
      return {
        items: newItems,
        tableNumber: prevCart?.tableNumber,
        note: prevCart?.note,
        subtotal: calculateCartSubtotal(newItems),
      }
    })
  }, [])

  const removeItem = useCallback((index: number) => {
    setCart((prevCart) => {
      if (!prevCart) return null
      const newItems = prevCart.items.filter((_, i) => i !== index)
      if (newItems.length === 0) return null
      return { ...prevCart, items: newItems, subtotal: calculateCartSubtotal(newItems) }
    })
  }, [])

  const updateQuantity = useCallback((index: number, quantity: number) => {
    if (quantity < 1 || quantity > MAX_QUANTITY) return
    setCart((prevCart) => {
      if (!prevCart) return null
      const newItems = [...prevCart.items]
      newItems[index] = { ...newItems[index], quantity }
      return { ...prevCart, items: newItems, subtotal: calculateCartSubtotal(newItems) }
    })
  }, [])

  const clearCart = useCallback(() => setCart(null), [])

  const itemCount = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0
  const subtotal = cart?.subtotal || 0

  return (
    <CartContext.Provider
      value={{ cart, addItem, removeItem, updateQuantity, clearCart, itemCount, subtotal }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart(): UseCartReturn {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
